import { useContext, useEffect, useState, useMemo } from 'react';
import Helmet from 'react-helmet';
import cx from 'classnames';
import Alert from '@/next-components/alert';
import Meta from '@/next-components/meta';
import { injectFontFaces } from '@/utils/inject-font-faces';
import useInputDevice from '@/hooks/useInputDevice';
import { ThemeContext } from '@/contexts/theme-context';

import { getCanUserUpdateBlockTheme } from '@/lib/api';
import { getStyles } from './styles/layout.css';
import Icons from './icons';

const CLASS_USING_MOUSE = 'using-mouse';
const CLASS_CAN_TOUCH = 'can-touch';

// remember: node can be undefined! see https://nextjs.org/docs/advanced-features/automatic-static-optimization#how-it-works
export default function Layout({
	node,
	preview,
	previewData = null,
	children,
	cssFonts,
}) {
	const [isUsingMouse, setIsUsingMouse] = useState(true);
	const [isTouchDevice, setIsTouchDevice] = useState(false);
	const [isLoaded, setIsLoaded] = useState(false);
	const [canUserUpdateBlockTheme, setCanUserUpdateBlockTheme] =
		useState(false);

	const htmlClasses = useMemo(() => {
		const { htmlAttributes } = Helmet?.peek() ?? {
			htmlAttributes: { class: '' },
		};
		const initialClasses = String(htmlAttributes.class)
			.split(' ')
			.reduce((c, i) => ({ ...c, [i]: true }), {});

		return cx(
			initialClasses,
			isUsingMouse && CLASS_USING_MOUSE,
			isTouchDevice && CLASS_CAN_TOUCH
		);
	}, [isUsingMouse, isTouchDevice]);

	const { rawTheme, theme } = useContext(ThemeContext);

	// update isUsingMouse on device change
	useInputDevice(({ touch, mouse }) => {
		setIsTouchDevice(touch);
		setIsUsingMouse(mouse);
	});

	// set css font faces globally (only once)
	useEffect(() => {
		if (cssFonts) {
			injectFontFaces(cssFonts);
		}
	}, []);

	useEffect(() => {
		setIsLoaded(true);
	}, []);

	// Check user capabilities - only if on preview mode
	useEffect(() => {
		if (!preview) return;
		// controller / signal are here to ensure we can
		// abort the request if componenent is unmounted
		// before the request is completed
		const controller = new AbortController();
		const signal = controller.signal;

		getCanUserUpdateBlockTheme(
			previewData.wpRestApiEndpoint,
			previewData.nonce,
			signal
		).then((can) => {
			setCanUserUpdateBlockTheme(can);
		});

		return () => controller.abort();
	}, []);

	return (
		<>
			<Helmet
				htmlAttributes={{
					class: htmlClasses,
				}}
			/>
			<Meta node={node} />
			<div
				className={cx(
					'app',
					{ isLoading: !isLoaded },
					getStyles(theme, rawTheme.tokens)
				)}
			>
				<Alert
					preview={preview}
					wpUrl={previewData?.wpUrl}
					editLink={node?.editLink}
					showCustomizerBtn={canUserUpdateBlockTheme}
				/>

				<div className="main">{children}</div>

				<Icons />
			</div>
		</>
	);
}
